import moment from 'moment';
import { DeliveryMethod } from './enums';

export const isArrayLength = arr => {
  return Array.isArray(arr) && arr.length > 0;
};

export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};

export const formatListingData = values => {
  const {
    photos,
    shippingPriceInSubunitsAdditionalItems,
    shippingPriceInSubunitsOneItem,
    isFreeShipping,
    deliveryOptions,
    stock,
    tags,
    ...rest
  } = values || {};

  const shippingEnabled =
    Array.isArray(deliveryOptions) && deliveryOptions.includes(DeliveryMethod.SHIPPING);

  return {
    ...rest,
    stockUpdate: {
      oldTotal: null,
      newTotal: stock,
    },
    publicData: {
      ...rest,
      tags,
      tagsKeyword: Array.isArray(tags) ? tags.join(',') : null,
      ...(shippingEnabled || isFreeShipping
        ? {
            deliveryOptions,
            shippingEnabled,
            shippingPriceInSubunitsOneItem: isFreeShipping
              ? null
              : shippingPriceInSubunitsOneItem?.amount ?? null,
            shippingPriceInSubunitsAdditionalItems: isFreeShipping
              ? null
              : shippingPriceInSubunitsAdditionalItems?.amount ?? null,
          }
        : {}),
    },
    photos,
  };
};

export const transformListingDataForDraft = data => {
  const {
    id,
    title,
    description,
    price,
    images, // 1. Extract images (generated by prepareListingValues)
    stockUpdate,
    publicData, // 2. Extract the publicData object (generated by prepareListingValues)
    geolocation,
    availabilityPlan,
    ...rest // Capture anything else
  } = data || {};

  return {
    // 3. Conditional ID: Only include if valid
    ...(id ? { id } : {}),

    title,
    description,
    ...(price ? { price } : {}),

    // 4. Images at top level (using the processed array)
    ...(images ? { images } : {}),

    // 5. Stock Update at top level
    ...(stockUpdate ? { stockUpdate } : {}),

    // 6. Public Data: Spread the existing object to avoid "publicData: { publicData: ... }"
    publicData: {
      ...publicData, // Flatten the publicData from the previous step
      ...(geolocation ? { geolocation } : {}),
      ...(availabilityPlan ? { availabilityPlan } : {}),
      // Merge any leftover fields if they aren't already in publicData
      ...rest,
    },
  };
};

const requiredFieldsByCategory = {
  dancewear: [
    'title',
    'description',
    'category',
    'type',
    'size',
    'brand',
    'color',
    'condition',
    'price',
    'stock',
    'deliveryOptions',
    'photos',
    'listingType',
    'transactionProcessAlias',
    'unitType',
  ],
  shoes: [
    'title',
    'description',
    'category',
    'type',
    'size',
    'brand',
    'color',
    'condition',
    'price',
    'stock',
    'deliveryOptions',
    'photos',
    'listingType',
    'transactionProcessAlias',
    'unitType',
  ],
  costumes: [
    'title',
    'description',
    'category',
    'type',
    'size',
    'brand',
    'color',
    'condition',
    'genre',
    'price',
    'stock',
    'deliveryOptions',
    'photos',
    'listingType',
    'transactionProcessAlias',
    'unitType',
  ],
};

const hasFieldValue = value => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return value !== null && value !== undefined;
};

/**
 * The required listing fields that are still empty, in form order. Without a category, only the
 * category is reported, since the rest of the list depends on it.
 */
export const getMissingProductFormFields = data => {
  const requiredFields = requiredFieldsByCategory[(data?.category)];
  if (!requiredFields) {
    return ['category'];
  }
  return requiredFields.filter(field => !hasFieldValue(data?.[field]));
};

export const isProductFormCompleteByCategory = data =>
  getMissingProductFormFields(data).length === 0;

export const getListingField = (config, fieldPath, keys) => {
  const keyList = Array.isArray(keys) ? keys : [keys];
  const fields = fieldPath.split('.').reduce((obj, segment) => obj?.[segment], config);
  if (!Array.isArray(fields)) {
    return [];
  }
  return keyList
    .map(key => fields.find(item => item?.key === key))
    .filter(Boolean)
    .map(item => ({ key: item.key, enumOptions: item.enumOptions || [] }));
};

export const getConfigOptions = (configEnumOptions, key, isPartialMatch = false) => {
  if (!Array.isArray(configEnumOptions) || !key) {
    return [];
  }
  return isPartialMatch
    ? configEnumOptions
        .filter(item => item?.key?.includes(key))
        .flatMap(item => item?.enumOptions || [])
    : configEnumOptions.find(item => item?.key === key)?.enumOptions || [];
};

/**
 * Formats the userLastActivity date for display.
 * @param {string} userLastActivity - The date string of the user's last activity (e.g., "2024-11-25 16:23:59").
 * @returns {string} - The formatted date string.
 */
export const formatLastActivityDate = userLastActivity => {
  if (!userLastActivity) return ''; // Handle empty or undefined values

  return moment(userLastActivity).calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Today]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: '[Last] dddd',
    sameElse: 'MMM D, YYYY',
  });
};

export const capitalizeFirstLetter = str => str && str[0].toUpperCase() + str.slice(1);
export const getListingOptionLabel = (options, key) => {
  if (!Array.isArray(options) || !key) {
    return '';
  }
  const foundOption = options.find(option => option?.option === key);
  return foundOption?.label || '';
};

export const combineCartItemsBySeller = listings => {
  return (listings || []).reduce((result, listing) => {
    const sellerId = listing?.author?.id?.uuid;
    if (!sellerId) {
      return result;
    }
    if (!result[sellerId]) {
      result[sellerId] = [];
    }
    result[sellerId].push(listing);
    return result;
  }, {});
};

export const transformArrayKeys = array => {
  return isArrayLength(array)
    ? array.map(item => {
        const newItem = { ...item }; // Create a shallow copy to avoid mutating the original object
        if ('values' in newItem) {
          newItem.key = newItem.values; // Assign the value from 'values' to 'key'
          delete newItem.values; // Remove the old key
        }
        return newItem;
      })
    : [];
};
